import React, { useMemo } from 'react'
import { Grid, Box } from '@material-ui/core'
import * as yup from 'yup'

import TextField from 'presentation/shared/components/TextField'
import TextArea from 'presentation/shared/components/TextArea'

import * as S from './styles'

import { FileViewProps, FileView } from './FileView'
import { UploadFile } from './UploadFile'
import Button from 'presentation/shared/components/Button'
import { newRegistrationDetailsContext } from 'presentation/hospital/layouts/DoctorRegister/NewRegistrationsDetails/context'

export interface FormDocumentEvent {
  title?: string
  description?: string
  sectionId: string
}

export interface FileUploadEvent {
  file: File
  sectionId: string
}

export interface FileDeleteEvent {
  fileId?: string
  sectionId: string
}

export interface FormDocumentProps extends FormDocumentEvent {
  doctor_document_validation_id?: number
  document_id?: number
  file?: Omit<FileViewProps, 'onFileUpload' | 'onFileDelete' | 'onFileView'>
  isTitleEditable?: boolean
  onDelete?: (sectionId: string) => void
  isEditable?: boolean
  canDelete?: boolean
  onChange: (evt: FormDocumentEvent) => void
  onFileUpload?: (evt: FileUploadEvent) => void
  onFileDelete?: (evt: FileDeleteEvent) => void
  onFileView?: (evt: string) => void
  onFileDownload?: (evt: string) => void
  onSave?: () => void
}

function FormDocument({
  file,
  isTitleEditable,
  isEditable,
  canDelete,
  sectionId,
  onDelete,
  title,
  description,
  onChange,
  onFileUpload,
  onFileDelete,
  onFileView,
  onSave,
  onFileDownload
}: FormDocumentProps) {
  const validationSchema = yup.object().shape({
    title: yup.string().required('Title is required'),
    description: yup.string().required('Description is required'),
    file: yup.mixed().required('File is required')
  })

  const isDirector = newRegistrationDetailsContext.isDirector

  const isValid = useMemo(() => {
    return validationSchema.isValidSync({
      title,
      description,
      file
    })
  }, [title, description, file])

  const handleChangeField = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const fieldValue = evt.target.value
    const fieldName = evt.target.name

    onChange({
      title,
      description,
      sectionId,
      [fieldName]: fieldValue
    })
  }

  const handleUploadFile = (evt: File) => {
    if (onFileUpload)
      onFileUpload({
        file: evt,
        sectionId
      })
  }

  const handleDeleteFile = (evt?: string) => {
    if (onFileDelete)
      onFileDelete({
        sectionId,
        fileId: evt || undefined
      })
  }

  const handleFileViewer = () => {
    if (onFileView) onFileView(sectionId)
  }

  return (
    <Box width="300px">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent={canDelete && 'space-between'}
          >
            <S.SectionTitle>Nome do documento</S.SectionTitle>
            {canDelete && (
              <S.SubtitledText
                onClick={() => {
                  if (onDelete) onDelete(sectionId)
                }}
              >
                Excluir seção
              </S.SubtitledText>
            )}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            onChange={handleChangeField}
            value={title}
            placeholder="Seu título aqui"
            label=""
            type="text"
            name="title"
            disabled={!isTitleEditable || !isEditable}
          />
        </Grid>
        <Grid item xs={12}>
          {file ? (
            <FileView
              {...file}
              isEditable={isEditable && !isDirector}
              onFileUpload={handleUploadFile}
              onFileDelete={handleDeleteFile}
              onFileView={handleFileViewer}
              onFileDownload={() => onFileDownload?.(sectionId)}
            />
          ) : (
            <UploadFile onFileUpload={handleUploadFile} sectionId={sectionId} />
          )}
        </Grid>
        <Grid item xs={12}>
          <TextArea
            onChange={(evt) =>
              handleChangeField(
                evt as unknown as React.ChangeEvent<HTMLInputElement>
              )
            }
            value={description}
            placeholder="Insira a descrição do arquivo"
            label="Descrição"
            type="text"
            name="description"
            required
            rows={4}
            disabled={!isEditable}
          />
        </Grid>
        {!isDirector && (
          <Grid item xs={12}>
            <Button onClick={onSave} disabled={!isValid}>
              Salvar
            </Button>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}

export { FormDocument }
